import PropTypes from 'prop-types';
import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { ReadOnlyFormControl } from 'components/common/fields';
import ButtonWithCircularProgress from 'components/common/ButtonWithCircularProgress';
import {
  cancelGuestScheduled,
  CANCEL_GUEST_SCHEDULED,
} from 'model/guestLoanSearch';
import { selector as busySelector } from 'selectors/busy';
import { getAuth } from 'util/index';
import {
  formatCurrency,
  formatDateMDY,
} from '../../History/config/DetailsRender/consts';

const useStyles = makeStyles((theme) => ({
  detailsSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  warningText: {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
}));

const GuestCancelPaymentDialog = ({
  open,
  payment,
  fspId,
  loanNumber,
  onClose,
  onCancelSuccess,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const auth = useSelector(getAuth);
  const isBusy = useSelector((state) =>
    busySelector(state, CANCEL_GUEST_SCHEDULED)
  );
  const [error, setError] = useState(null);
  const amountValue = Number(payment?.amount) || 0;
  const feeAmountValue = Number(payment?.feeAmount) || 0;
  const totalAmountValue = amountValue + feeAmountValue;

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleCancel = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (!payment || !fspId) return;
    if (!payment.payerId || !payment.id) {
      return;
    }
    setError(null);
    try {
      await dispatch(
        cancelGuestScheduled({
          fspId,
          loanNumber,
          payerId: payment.payerId,
          loanSchedId: payment.id,
          userId: payment.insUserId,
          auth,
        })
      );

      setError(null);
      if (onCancelSuccess) onCancelSuccess(payment.id);
      onClose();
    } catch (e) {
      setError(e);
    }
  }, [dispatch, payment, fspId, loanNumber, auth, onCancelSuccess, onClose]);

  if (!open || !payment) return null;

  return (
    <Dialog
      open={open}
      onClose={!isBusy ? handleCancel : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Cancel Scheduled Payment</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel this scheduled payment? This action
          cannot be undone.
        </DialogContentText>
        <div className={classes.detailsSection}>
          <Typography variant="subtitle2" gutterBottom>
            Payment Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ReadOnlyFormControl
                label="Scheduled ID"
                value={payment.id || ''}
              />
            </Grid>
            <Grid item xs={6}>
              <ReadOnlyFormControl
                label="Confirmation #"
                value={payment.confirmationNumber || 'N/A'}
              />
            </Grid>
            <Grid item xs={6}>
              <ReadOnlyFormControl
                label="Send Date"
                value={formatDateMDY(payment.sendOn)}
              />
            </Grid>
            <Grid item xs={6}>
              <ReadOnlyFormControl
                label="Amount"
                value={formatCurrency(amountValue)}
              />
            </Grid>
            {feeAmountValue > 0 && (
              <Grid item xs={6}>
                <ReadOnlyFormControl
                  label="Fee Amount"
                  value={formatCurrency(feeAmountValue)}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <ReadOnlyFormControl
                label="Total Amount"
                value={formatCurrency(totalAmountValue)}
              />
            </Grid>
            <Grid item xs={12}>
              <ReadOnlyFormControl
                label="Loan Number"
                value={loanNumber || ''}
              />
            </Grid>
          </Grid>
        </div>

        <Typography variant="body2" className={classes.warningText}>
          The payer will receive an email notification about this cancellation.
        </Typography>
        {error && (
          <Typography color="error" variant="body2">
            Sorry, but we&apos;ve had an error. Please try again.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary" disabled={isBusy}>
          No, Keep Payment
        </Button>
        <ButtonWithCircularProgress
          busy={isBusy}
          color="secondary"
          onClick={handleConfirm}
          variant="contained"
        >
          Yes, Cancel Payment
        </ButtonWithCircularProgress>
      </DialogActions>
    </Dialog>
  );
};

GuestCancelPaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  payment: PropTypes.object,
  fspId: PropTypes.number,
  loanNumber: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCancelSuccess: PropTypes.func,
};

GuestCancelPaymentDialog.defaultProps = {
  payment: null,
  fspId: null,
  loanNumber: null,
  onCancelSuccess: undefined,
};

export default GuestCancelPaymentDialog;
